import registry from './readFields.json';

/**
 * Editing of a read answer (the device forms): which fields may be set on a protocol, how an edited value goes back to
 * the gateway (same shape as the read answer, bit sets as integers, compact rule windows as HHMM), what changed, and
 * whether the read-back shows the saved value. Only fields whose registry entry has `set[<protocol>]` are editable:
 * a documented, implemented encoder in the configure class (scripts/gen-read-fields.mjs).
 */
export type Json = null | boolean | number | string | Json[] | { [k: string]: Json };
export type Obj = { [k: string]: Json };
export type EditSpec = {
  capability: string;
  min?: number;
  max?: number;
  step?: number;
  /** bit names by position (null = unused bit) */
  bits?: (string | null)[];
  /** writable bits of a mask (registry field spec; some protocols mask the upper bits) */
  mask?: number;
  members?: Record<string, { min?: number; max?: number; step?: number }>;
};
type FieldSpec = {
  kind?: string;
  min?: number;
  max?: number;
  special?: number[];
  set?: Record<string, EditSpec>;
};

const FIELDS = registry.fields as unknown as Record<string, FieldSpec>;
export const isObj = (v: Json | undefined): v is Obj =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

export function editSpec(key: string, protocol: string | undefined): EditSpec | undefined {
  return protocol ? FIELDS[key]?.set?.[protocol] : undefined;
}

/** A value the form can show as a control (not a special raw value or one outside the documented range). */
function clean(key: string, v: Json): boolean {
  const s = FIELDS[key];
  if (Array.isArray(v)) return v.every((x) => clean(key, x));
  if (typeof v !== 'number' || !s) return true;
  if (s.special?.includes(v)) return false;
  if (s.min !== undefined && s.max !== undefined && (v < s.min || v > s.max)) return false;
  return true;
}

/** Editable on this protocol, through a capability the caller may send, with a current value the form understands. */
export function editable(
  key: string,
  value: Json,
  protocol: string | undefined,
  writes: ReadonlySet<string>,
): boolean {
  const e = editSpec(key, protocol);
  return !!e && writes.has(e.capability) && clean(key, value);
}

const bitsToInt = (v: Obj, e: EditSpec): number => {
  const base = Number(v.raw ?? v.mask ?? 0);
  let n = e.mask !== undefined ? base & e.mask : base;
  const bits = (v.bits ?? {}) as Record<string, boolean>;
  (e.bits ?? []).forEach((name, i) => {
    if (!name || !(name in bits)) return;
    n = bits[name] ? n | (1 << i) : n & ~(1 << i);
  });
  return n >>> 0;
};

/** The value as the gateway's config.write expects it. */
export function toWire(key: string, v: Json, protocol: string | undefined): Json {
  const e = editSpec(key, protocol);
  if (Array.isArray(v)) return v.map((x) => toWire(key, x, protocol));
  if (e?.bits && isObj(v)) return bitsToInt(v, e);
  return v;
}

/** Deep equality of decoded values (numbers compared with a tolerance for scaled values). */
export function same(a: Json | undefined, b: Json | undefined): boolean {
  if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) < 1e-6;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => same(x, b[i]));
  if (isObj(a) && isObj(b)) {
    // bit sets: only the named bits matter (raw/mask may carry bits the form does not show)
    if (isObj(a.bits) && isObj(b.bits)) return same(a.bits, b.bits);
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...keys].every((k) => same(a[k], b[k]));
  }
  return a === b;
}

export type Change = { key: string; from: Json; to: Json };
export function changes(original: Obj, draft: Obj): Change[] {
  return Object.keys(draft)
    .filter((k) => !same(original[k], draft[k]))
    .map((k) => ({ key: k, from: original[k] ?? null, to: draft[k]! }));
}

/** Problems the form can see before sending (the gateway validates again). */
export function validate(key: string, v: Json, protocol: string | undefined): string | null {
  const e = editSpec(key, protocol);
  if (!e) return 'not_editable';
  const check = (x: Json, lim: { min?: number; max?: number; step?: number }): string | null => {
    if (typeof x !== 'number') return null;
    if (!Number.isFinite(x)) return 'number';
    if (lim.min !== undefined && x < lim.min) return 'min';
    if (lim.max !== undefined && x > lim.max) return 'max';
    if (lim.step && Math.abs(x / lim.step - Math.round(x / lim.step)) > 1e-6) return 'step';
    return null;
  };
  if (Array.isArray(v)) return v.map((x) => check(x, e)).find((x) => x) ?? null;
  if (isObj(v) && e.members)
    return (
      Object.entries(e.members)
        .map(([m, lim]) => check(v[m] ?? null, lim))
        .find((x) => x) ?? null
    );
  if (typeof v === 'string' && FIELDS[key]?.kind === 'time' && !/^\d\d:\d\d$/.test(v)) return 'time';
  return check(v, e);
}

/** Parameter names the device may put in a report list (registry `reportable`: readable parameters), sorted. */
export function reportable(): string[] {
  return Object.entries(registry.fields as Record<string, { reportable?: boolean }>)
    .filter(([, s]) => s.reportable)
    .map(([k]) => k)
    .sort();
}

/** At most 20 parameters per report list (vendor SDK limit, docs/protocols; report profiles: catalog report_max_keys). */
export const REPORT_LIST_MAX = 20;
